import type {SortingState, ColumnSort, DeepKeys} from '@tanstack/react-table';
import {z} from 'zod';

/**
 *
 * @param params The sorting state as we represent it in our UI (borrowed from `@tanstack/react-table`)
 * @returns The sorting state as we represent it in our API
 * @example transformSort([{id: 'name', desc: true}]) => ['-name']
 */
export const transformSort = (params: SortingState) => {
  return params.map(param => (param.desc ? `-${param.id}` : param.id)) satisfies Array<string | `-${string}`>;
};

/**
 *
 * @param sort The sorting state as we represent it in our API
 * @returns The sorting state as we represent it in our UI (borrowed from `@tanstack/react-table`)
 * @example queryToSort('-name') => [{id: 'name', desc: true}]
 */
export const queryToSort = (query: string): SortingState => {
  if (query.length === 0) {
    return [];
  }

  return query
    .split(`,`)
    .filter(value => value.length !== 0)
    .map(value => {
      const desc = value.charAt(0) === `-`;

      return {id: desc ? value.substring(1) : value, desc} as ColumnSort;
    }) as SortingState;
};

/**
 * Converts a sorting state to a query string
 * @param sort The sorting state as we represent it in our UI (borrowed from `@tanstack/react-table`)
 * @returns The sorting state as we represent it in our API
 * @example sortToQuery([{id: 'name', desc: true}]) => '-name'
 **/
export const sortToQuery = (sort: SortingState = []): string => {
  if (!sort.length) return ``;
  return transformSort(sort).join(`,`);
};

type SortArray<T> = Array<`-${DeepKeys<T>}` | DeepKeys<T>>;

/**
 *
 * @param sorting  A simple sorting array
 * @returns  A helper object that can be converted to a string
 * @example sortHelper(['-name', 'age']).asString() => '-name,age'
 * @example sortHelper<WorkflowApiModel>(['-total_runs']).asString() => '-total_runs'
 */
export const sortHelper = <T extends object>(sorting: SortArray<T>) => ({
  asString: () => sorting.join(`,`) as string,
  asState: () => queryToSort(sorting.join(`,`)),
});

export const isSortingState = (value: unknown): value is SortingState => {
  return z.array(z.object({id: z.string(), desc: z.boolean()})).safeParse(value)?.success;
};
